import { useCallback } from 'react'

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { useAuthInfo } from '@sweetauth/core'

import { API_URL, MCP_ENABLED } from '@/constants/env'

import { SweetspotError } from '@/helpers/error'

import { useHandleError } from '@/hooks/common/useErrorHandler'
import {
  availableMcpServersQueryKeys,
  enabledMcpServersQueryKeys,
} from '@/hooks/mcp/types'

import type { UUID } from '@/types/Id'
import type { OrganizationMCPServer } from '@/types/MCPServer'

export const MICROSOFT_CAPABILITIES_REQUIRED_ERROR_MESSAGE =
  'Select at least one capability before continuing'

const fetchOrganizationMCPServers = async (
  orgId: UUID | '',
  accessToken: string | undefined | null,
): Promise<OrganizationMCPServer[]> => {
  if (accessToken === null || accessToken === undefined) {
    throw new Error(`No access token provided.`)
  }

  if (orgId === '') {
    throw new Error(`Organization ID is required.`)
  }

  const res: Response = await fetch(
    `${API_URL}/api/v1/organizations/${orgId}/mcp-servers`,
    {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
    },
  )

  if (!res.ok) {
    throw new SweetspotError({
      name: 'FETCH_ERROR',
      message: `Failed to fetch organization MCP servers: ${res.statusText}`,
      cause: {
        status: res.status,
      },
    })
  }

  return (await res.json()) as OrganizationMCPServer[]
}

interface UseOrganizationMCPServersArgs<T> {
  orgId: UUID | ''
  select?: (data: OrganizationMCPServer[]) => T
}

export const useOrganizationMCPServers = <T = OrganizationMCPServer[]>({
  orgId,
  select,
}: UseOrganizationMCPServersArgs<T>) => {
  const { accessToken } = useAuthInfo()

  const selectFn = useCallback(
    (data: OrganizationMCPServer[]): T => {
      return select ? select(data) : (data as unknown as T)
    },
    [select],
  )

  return useQuery<OrganizationMCPServer[], Error, T>({
    queryKey: enabledMcpServersQueryKeys.organization({ orgId: orgId }),
    queryFn: () => fetchOrganizationMCPServers(orgId, accessToken),
    enabled: !!accessToken && !!orgId && MCP_ENABLED,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    select: selectFn,
  })
}

const selectMicrosoftOrgServer = (orgServers: OrganizationMCPServer[]) => {
  return orgServers.find(
    (s) => s.mcp_server.server_name === 'microsoft' && s.is_active,
  )
}

const selectSalesforceOrgServer = (orgServers: OrganizationMCPServer[]) => {
  return orgServers.find(
    (s) => s.mcp_server.server_name === 'salesforce' && s.is_active,
  )
}

const selectHasSalesforceOrgServerToken = (
  orgServers: OrganizationMCPServer[],
) => {
  return orgServers.some(
    (s) =>
      s.mcp_server.server_name === 'salesforce' &&
      s.is_active &&
      s.user_has_token,
  )
}

export const useMicrosoftOrgServer = ({
  orgId,
}: Omit<
  UseOrganizationMCPServersArgs<OrganizationMCPServer | undefined>,
  'select'
>) => {
  return useOrganizationMCPServers({ orgId, select: selectMicrosoftOrgServer })
}

export const useSalesforceOrgServer = ({
  orgId,
}: Omit<
  UseOrganizationMCPServersArgs<OrganizationMCPServer | undefined>,
  'select'
>) => {
  return useOrganizationMCPServers({ orgId, select: selectSalesforceOrgServer })
}

export const useHasSalesforceOrgServerToken = ({
  orgId,
}: Omit<UseOrganizationMCPServersArgs<boolean>, 'select'>) => {
  return useOrganizationMCPServers({
    orgId,
    select: selectHasSalesforceOrgServerToken,
  })
}

interface EnableMCPServerRequest {
  oauth_client_id?: string
  oauth_client_secret?: string
  oauth_provider_config?: Record<string, any>
  enabled_toolset_ids?: UUID[]
}

interface EnableMCPServerContext {
  previousServers: OrganizationMCPServer[] | undefined
}

interface EnableMCPServerArgs {
  mcpServerId: UUID
  request: EnableMCPServerRequest
}

const enableMCPServer = async (
  orgId: UUID | '',
  mcpServerId: UUID,
  request: EnableMCPServerRequest,
  accessToken: string | undefined | null,
): Promise<OrganizationMCPServer> => {
  if (orgId === '') {
    throw new Error(`Organization ID is required.`)
  }

  if (accessToken === null || accessToken === undefined) {
    throw new Error(`No access token provided.`)
  }

  const res: Response = await fetch(
    `${API_URL}/api/v1/organizations/${orgId}/mcp-servers/${mcpServerId}`,
    {
      method: 'POST',
      body: JSON.stringify(request),
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
    },
  )

  if (!res.ok) {
    if (res.status === 409) {
      throw new SweetspotError({
        name: 'FETCH_ERROR',
        message: await extractErrorMessage(
          res,
          'This integration is already enabled',
        ),
        cause: {
          status: res.status,
        },
      })
    }
    throw new SweetspotError({
      name: 'FETCH_ERROR',
      message: await extractErrorMessage(
        res,
        `Failed to enable MCP server: ${res.statusText}`,
      ),
      cause: {
        status: res.status,
      },
    })
  }

  return (await res.json()) as OrganizationMCPServer
}

interface UseEnableMCPServerArgs {
  orgId: UUID | ''
}

export const useEnableMCPServer = ({ orgId }: UseEnableMCPServerArgs) => {
  const user = useAuthInfo()
  const queryClient = useQueryClient()
  const { handleError } = useHandleError()

  return useMutation<
    OrganizationMCPServer,
    Error,
    EnableMCPServerArgs,
    EnableMCPServerContext
  >({
    mutationFn: ({ mcpServerId, request }) =>
      enableMCPServer(orgId, mcpServerId, request, user?.accessToken),
    onMutate: async ({ mcpServerId, request }) => {
      // Cancel any outgoing refetches
      const queryKey = enabledMcpServersQueryKeys.organization({
        orgId: orgId,
      })
      await queryClient.cancelQueries({
        queryKey: queryKey,
      })

      // Snapshot the previous value
      const previousServers =
        queryClient.getQueryData<OrganizationMCPServer[]>(queryKey)

      if (!!orgId) {
        // Optimistically update to the new value
        queryClient.setQueryData<OrganizationMCPServer[]>(queryKey, (old) => [
          ...(old || []),
          {
            id: crypto.randomUUID() as UUID, // Temporary ID
            organization_id: orgId,
            mcp_server_id: mcpServerId,
            oauth_client_id: request.oauth_client_id || null,
            has_client_secret: !!request.oauth_client_secret,
            oauth_provider_config: request.oauth_provider_config || null,
            is_active: true,
            mcp_server: {
              id: mcpServerId,
              server_name: '',
              display_name: '',
              description: null,
              auth_type: null,
              requires_auth: false,
              server_url: '',
              oauth_required_params: undefined,
            },
          },
        ])
      }

      // Return a context object with the snapshot value
      return { previousServers }
    },
    onError: (err, variables, context) => {
      // If the mutation fails, use the context returned from onMutate to roll back
      if (context?.previousServers && !!orgId) {
        queryClient.setQueryData(
          enabledMcpServersQueryKeys.organization({ orgId: orgId }),
          context.previousServers,
        )
      }
      if (
        err instanceof SweetspotError &&
        err.message === MICROSOFT_CAPABILITIES_REQUIRED_ERROR_MESSAGE
      ) {
        return
      }
      handleError(
        err,
        false,
        err instanceof SweetspotError ? err.message : undefined,
      )
    },
    onSettled: () => {
      // Always refetch after error or success
      void queryClient.invalidateQueries({
        queryKey: availableMcpServersQueryKeys.organization({ orgId: orgId }),
      })
      return queryClient.invalidateQueries({
        queryKey: enabledMcpServersQueryKeys.organization({ orgId: orgId }),
      })
    },
  })
}

const disableMCPServer = async (
  orgId: UUID | '',
  mcpServerId: UUID,
  accessToken: string | undefined | null,
): Promise<void> => {
  if (accessToken === null || accessToken === undefined) {
    throw new Error(`No access token provided.`)
  }

  if (orgId === '') {
    throw new Error(`Organization ID is required.`)
  }

  const res: Response = await fetch(
    `${API_URL}/api/v1/organizations/${orgId}/mcp-servers/${mcpServerId}`,
    {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
    },
  )

  if (!res.ok) {
    throw new SweetspotError({
      name: 'FETCH_ERROR',
      message: `Failed to disable MCP server: ${res.statusText}`,
      cause: {
        status: res.status,
      },
    })
  }
}

interface UseDisableMCPServerArgs {
  orgId: UUID | ''
}

export const useDisableMCPServer = ({ orgId }: UseDisableMCPServerArgs) => {
  const { accessToken } = useAuthInfo()
  const queryClient = useQueryClient()
  const { handleError } = useHandleError()

  return useMutation<void, Error, UUID, EnableMCPServerContext>({
    mutationFn: (mcpServerId: UUID) =>
      disableMCPServer(orgId, mcpServerId, accessToken),
    onMutate: async (mcpServerId) => {
      // Cancel any outgoing refetches
      const queryKey = enabledMcpServersQueryKeys.organization({ orgId: orgId })
      await queryClient.cancelQueries({
        queryKey: enabledMcpServersQueryKeys.organization({ orgId: orgId }),
      })

      // Snapshot the previous value
      const previousServers =
        queryClient.getQueryData<OrganizationMCPServer[]>(queryKey)

      // Optimistically remove the server
      queryClient.setQueryData<OrganizationMCPServer[]>(queryKey, (old) =>
        (old || []).filter((server) => server.mcp_server_id !== mcpServerId),
      )

      // Return a context object with the snapshot value
      return { previousServers }
    },
    onError: (err, variables, context) => {
      // If the mutation fails, use the context returned from onMutate to roll back
      if (context?.previousServers) {
        queryClient.setQueryData(
          enabledMcpServersQueryKeys.organization({ orgId: orgId }),
          context.previousServers,
        )
      }
      handleError(err)
    },
    onSettled: () => {
      // Always refetch after error or success
      void queryClient.invalidateQueries({
        queryKey: availableMcpServersQueryKeys.organization({ orgId: orgId }),
      })
      return queryClient.invalidateQueries({
        queryKey: enabledMcpServersQueryKeys.organization({ orgId: orgId }),
      })
    },
  })
}

interface UpdateMCPServerRequest {
  oauth_provider_config?: Record<string, any>
  enabled_toolset_ids?: UUID[]
}

const extractErrorMessage = async (
  res: Response,
  fallback: string,
): Promise<string> => {
  const contentType = res.headers.get('content-type') || ''

  try {
    if (contentType.includes('application/json')) {
      const json = (await res.json()) as { detail?: string }
      if (json.detail) return json.detail
    } else {
      const text = await res.text()
      if (text) return text
    }
  } catch {
    return fallback
  }

  return fallback
}

const updateMCPServer = async (
  orgId: UUID | '',
  mcpServerId: UUID,
  request: UpdateMCPServerRequest,
  accessToken: string | undefined | null,
): Promise<OrganizationMCPServer> => {
  if (accessToken === null || accessToken === undefined) {
    throw new Error(`No access token provided.`)
  }

  if (orgId === '') {
    throw new Error(`Organization ID is required.`)
  }

  const res: Response = await fetch(
    `${API_URL}/api/v1/organizations/${orgId}/mcp-servers/${mcpServerId}`,
    {
      method: 'PATCH',
      body: JSON.stringify(request),
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
    },
  )

  if (!res.ok) {
    throw new SweetspotError({
      name: 'FETCH_ERROR',
      message: await extractErrorMessage(
        res,
        `Failed to update MCP server: ${res.statusText}`,
      ),
      cause: {
        status: res.status,
      },
    })
  }

  return (await res.json()) as OrganizationMCPServer
}

interface UseUpdateMCPServerArgs {
  orgId: UUID | ''
}

interface UpdateMCPServerArgs {
  mcpServerId: UUID
  request: UpdateMCPServerRequest
}

export const useUpdateMCPServer = ({ orgId }: UseUpdateMCPServerArgs) => {
  const { accessToken } = useAuthInfo()
  const queryClient = useQueryClient()
  const { handleError } = useHandleError()

  return useMutation<
    OrganizationMCPServer,
    Error,
    UpdateMCPServerArgs,
    EnableMCPServerContext
  >({
    mutationFn: ({ mcpServerId, request }) =>
      updateMCPServer(orgId, mcpServerId, request, accessToken),
    onSuccess: () => {
      // Invalidate the organization MCP servers query to refresh the list
      return queryClient.invalidateQueries({
        queryKey: enabledMcpServersQueryKeys.organization({ orgId: orgId }),
      })
    },
    onError: (err) =>
      handleError(
        err,
        false,
        err instanceof SweetspotError ? err.message : undefined,
      ),
  })
}

interface SetCustomCredentialsRequest {
  oauth_client_id: string
  oauth_client_secret?: string
  oauth_redirect_uri?: string
  oauth_provider_config?: Record<string, any>
}

const setCustomCredentials = async (
  orgId: UUID | '',
  mcpServerId: UUID,
  request: SetCustomCredentialsRequest,
  accessToken: string | undefined | null,
): Promise<OrganizationMCPServer> => {
  if (accessToken === null || accessToken === undefined) {
    throw new Error(`No access token provided.`)
  }

  if (orgId === '') {
    throw new Error(`Organization ID is required.`)
  }

  const res: Response = await fetch(
    `${API_URL}/api/v1/organizations/${orgId}/mcp-servers/${mcpServerId}/custom-credentials`,
    {
      method: 'PUT',
      body: JSON.stringify(request),
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
    },
  )

  if (!res.ok) {
    throw new SweetspotError({
      name: 'FETCH_ERROR',
      message: await extractErrorMessage(
        res,
        `Failed to set custom credentials: ${res.statusText}`,
      ),
      cause: {
        status: res.status,
      },
    })
  }

  return (await res.json()) as OrganizationMCPServer
}

interface SetCustomCredentialsArgs {
  mcpServerId: UUID
  request: SetCustomCredentialsRequest
}

interface UseSetCustomCredentialsArgs {
  orgId: UUID | ''
}

export const useSetCustomCredentials = ({
  orgId,
}: UseSetCustomCredentialsArgs) => {
  const { accessToken } = useAuthInfo()
  const queryClient = useQueryClient()
  const { handleError } = useHandleError()

  return useMutation<
    OrganizationMCPServer,
    Error,
    SetCustomCredentialsArgs,
    EnableMCPServerContext
  >({
    mutationFn: ({ mcpServerId, request }) =>
      setCustomCredentials(orgId, mcpServerId, request, accessToken),
    onSuccess: () => {
      return queryClient.invalidateQueries({
        queryKey: enabledMcpServersQueryKeys.organization({ orgId: orgId }),
      })
    },
    onError: (err) =>
      handleError(
        err,
        false,
        err instanceof SweetspotError ? err.message : undefined,
      ),
  })
}

const clearCustomCredentials = async (
  orgId: UUID | '',
  mcpServerId: UUID,
  accessToken: string | undefined | null,
): Promise<OrganizationMCPServer> => {
  if (accessToken === null || accessToken === undefined) {
    throw new Error(`No access token provided.`)
  }

  if (orgId === '') {
    throw new Error(`Organization ID is required.`)
  }

  const res: Response = await fetch(
    `${API_URL}/api/v1/organizations/${orgId}/mcp-servers/${mcpServerId}/custom-credentials`,
    {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
    },
  )

  if (!res.ok) {
    throw new SweetspotError({
      name: 'FETCH_ERROR',
      message: await extractErrorMessage(
        res,
        `Failed to clear custom credentials: ${res.statusText}`,
      ),
      cause: {
        status: res.status,
      },
    })
  }

  return (await res.json()) as OrganizationMCPServer
}

interface UseClearCustomCredentialsArgs {
  orgId: UUID | ''
}

export const useClearCustomCredentials = ({
  orgId,
}: UseClearCustomCredentialsArgs) => {
  const { accessToken } = useAuthInfo()
  const queryClient = useQueryClient()
  const { handleError } = useHandleError()

  return useMutation<
    OrganizationMCPServer,
    Error,
    UUID,
    EnableMCPServerContext
  >({
    mutationFn: (mcpServerId: UUID) =>
      clearCustomCredentials(orgId, mcpServerId, accessToken),
    onSuccess: () => {
      return queryClient.invalidateQueries({
        queryKey: enabledMcpServersQueryKeys.organization({ orgId: orgId }),
      })
    },
    onError: (err) =>
      handleError(
        err,
        false,
        err instanceof SweetspotError ? err.message : undefined,
      ),
  })
}

const disconnectMCPToken = async (
  mcpServerId: UUID,
  orgId: UUID | '',
  accessToken: string | undefined | null,
): Promise<void> => {
  if (accessToken === null || accessToken === undefined) {
    throw new Error('No access token provided.')
  }

  const res: Response = await fetch(
    `${API_URL}/api/v1/mcp-tokens/${mcpServerId}`,
    {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
        ...(orgId && { 'X-Organization-Id': orgId }),
      },
    },
  )

  if (!res.ok) {
    if (res.status === 404) {
      // Token already gone — treat as success
      return
    }
    throw new SweetspotError({
      name: 'FETCH_ERROR',
      message: `Failed to disconnect integration: ${res.statusText}`,
      cause: { status: res.status },
    })
  }
}

interface UseDisconnectMCPTokenArgs {
  orgId: UUID | ''
}

export const useDisconnectMCPToken = ({ orgId }: UseDisconnectMCPTokenArgs) => {
  const { accessToken } = useAuthInfo()
  const queryClient = useQueryClient()
  const { handleError } = useHandleError()

  return useMutation<void, Error, UUID>({
    mutationFn: (mcpServerId: UUID) =>
      disconnectMCPToken(mcpServerId, orgId, accessToken),
    onMutate: async (mcpServerId) => {
      const queryKey = enabledMcpServersQueryKeys.organization({ orgId })
      await queryClient.cancelQueries({ queryKey })

      // Optimistically mark user_has_token as false
      queryClient.setQueryData<OrganizationMCPServer[]>(queryKey, (old) =>
        (old || []).map((server) =>
          server.mcp_server_id === mcpServerId
            ? { ...server, user_has_token: false, needs_reauth: false }
            : server,
        ),
      )
    },
    onSettled: () => {
      return queryClient.invalidateQueries({
        queryKey: enabledMcpServersQueryKeys.organization({ orgId }),
      })
    },
    onError: (err) => handleError(err),
  })
}
